import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSubmenu,
  SidebarSubmenuItem,
  SidebarSpace,
} from '@backstage/core-components';
import { NavContentBlueprint, type NavContentNavItem, type NavContentNavItems } from '@backstage/plugin-app-react';
import { SidebarLogo } from './SidebarLogo';
import SearchIcon from '@material-ui/icons/Search';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { Settings as SidebarSettings, UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { IconComponent, useApp } from '@backstage/core-plugin-api';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import MapIcon from '@material-ui/icons/MyLocation';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import MenuBook from '@material-ui/icons/MenuBook';
import CallSplit from '@material-ui/icons/CallSplit';
import CategoryIcon from '@material-ui/icons/Category';
import LayersIcon from '@material-ui/icons/Layers';
import Score from '@material-ui/icons/Score';
import LiveHelp from '@material-ui/icons/LiveHelpOutlined';
import BuildIcon from '@material-ui/icons/Build';
import AssistantDirectionIcon from '@material-ui/icons/Directions';
import { InfraWalletIcon } from '@electrolux-oss/plugin-infrawallet';
import { TestkubeIcon } from '@internal/backstage-plugin-testkube';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import AssignmentIcon from '@material-ui/icons/Assignment';
import { ReportPortalIcon } from '@backstage-community/plugin-report-portal';
import { MCPChatIcon } from '@backstage-community/plugin-mcp-chat';
import { TemplateDesignerIcon } from "@tduniec/plugin-template-designer";
 
function SidebarNav({ navItems }: { navItems?: NavContentNavItems }) {
      // navItems may be undefined when no NFS page extensions provide nav items
      const nav = navItems?.withComponent((item: NavContentNavItem) => (
        <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
      ));

      // Take all items we render manually to prevent duplicates in rest()
      nav?.take('page:home');
      nav?.take('page:explore');
      nav?.take('page:catalog');
      nav?.take('page:api-docs');
      nav?.take('page:techdocs');
      nav?.take('page:scaffolder');
      nav?.take('page:search');
      nav?.take('page:tech-radar');
      nav?.take('page:catalog-import');
      nav?.take('page:user-settings');
      nav?.take('page:notifications');
      nav?.take('page:mcp-chat');
      nav?.take('page:toolbox');
      nav?.take('page:template-designer');

      const app = useApp();

      return (
        <Sidebar>
              <SidebarLogo />
              <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
                <SidebarSearchModal />
              </SidebarGroup>
              <SidebarDivider />
              {/* Global nav, not org-specific */}
              <SidebarItem icon={HomeIcon} to="/" text="Home" />
              <SidebarItem icon={LayersIcon} to="explore" text="Explore" />
              <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog">
                <SidebarSubmenu title="Catalog">
                  <SidebarSubmenuItem
                    title="Domains"
                    to="catalog?filters[kind]=domain"
                    icon={app.getSystemIcon('kind:domain')}
                  />
                  <SidebarSubmenuItem
                    title="Systems"
                    to="catalog?filters[kind]=system"
                    icon={app.getSystemIcon('kind:system')}
                  />
                  <SidebarSubmenuItem
                    title="Components"
                    to="catalog?filters[kind]=component"
                    icon={app.getSystemIcon('kind:component')}
                  />
                  <SidebarSubmenuItem
                    title="APIs"
                    to="catalog?filters[kind]=api"
                    icon={app.getSystemIcon('kind:api')}
                  />
                  <SidebarDivider />
                  <SidebarSubmenuItem
                    title="Resources"
                    to="catalog?filters[kind]=resource"
                    icon={app.getSystemIcon('kind:resource')}
                  />
                  <SidebarDivider />
                  <SidebarSubmenuItem
                    title="Groups"
                    to="catalog?filters[kind]=group"
                    icon={app.getSystemIcon('kind:group')}
                  />
                  <SidebarSubmenuItem
                    title="Users"
                    to="catalog?filters[kind]=user"
                    icon={app.getSystemIcon('kind:user')}
                  />
                </SidebarSubmenu>
              </SidebarItem>
                    <SidebarItem
                icon={MenuBook}
                to="https://metadata.alsenseplatform.com"
                text="Data-Catalog"
              />
              <SidebarItem text="APIs" to="api-docs" icon={ExtensionIcon} />
              <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
              <SidebarItem icon={CreateComponentIcon} to="create" text="Create" />
              {/* End global nav */}
              <SidebarDivider />
              <SidebarItem icon={AssistantDirectionIcon} to="roadmap" text="Roadmap" />
              <SidebarItem icon={LiveHelp} to="qeta" text="Q&A" />
              <SidebarItem icon={MCPChatIcon} to="mcp-chat" text="Copilot" />
              <SidebarItem icon={BuildIcon} to="toolbox" text="ToolBox" />
              <SidebarDivider />
              <SidebarScrollWrapper>
                <SidebarItem icon={MapIcon} to="tech-radar" text="Tech Radar" />
              </SidebarScrollWrapper>
              <SidebarItem icon={Score} to="score-board" text="Tech-Insights" />
              <SidebarItem icon={InfraWalletIcon} to="infrawallet" text="InfraWallet" />
              <SidebarItem icon={TestkubeIcon} to="testkube" text="Testkube" />
              <SidebarItem icon={VpnKeyIcon} to="ai-gateway" text="AI Gateway" />
              <SidebarItem icon={ReportPortalIcon as IconComponent} to="report-portal" text="Report Portal" />
              <SidebarDivider />
              <SidebarItem icon={CallSplit} to="coroot" text="Coroot" />
              <SidebarItem icon={AssignmentIcon} to="report-incident" text="Report an incident" />
              {/* Auto-discovered NFS plugins not manually rendered above {nav?.rest({ sortBy: 'title' })} */}
              <SidebarSpace />
              <SidebarDivider />
              <NotificationsSidebarItem />
              <SidebarDivider />
              <SidebarItem icon={TemplateDesignerIcon} to="template-designer" text="Template Designer" />
              <SidebarGroup
                label="Settings"
                icon={<UserSettingsSignInAvatar />}
                to="/settings"
              >
                <SidebarSettings />
              </SidebarGroup>
            </Sidebar>
      );
}

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: SidebarNav,
  },
});
